import type { Editor as CoreEditor } from '@ckeditor/ckeditor5-core'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import { FC, useState, useEffect, useId, useRef } from 'react'
import { Control, Controller } from 'react-hook-form'

import FormGroup from './FormGroup'
import { Variable } from '../../common/models'
import { InsertVariableModal } from '../../home/components/settings/email-templates/InsertVariable'
import { getEmailTemplateVelocityPreview } from '../services/email-templates'
import { Button, Checkbox, Textarea } from '@/components/ui'
import { cn } from '@/lib/utils'
import { PageTitle } from '@/components'

type Props = {
  label?: string | React.ReactNode
  variables: Variable[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>
  name: string
  isError: boolean
  value: string
  checkboxLabel?: string
  checkboxWrapperClassName?: string
  onChange: (data: string) => void
  disabled?: boolean
  index: number
  defaultCustomHTML?: boolean
  hideCheckbox?: boolean
  showPreview?: boolean
  showLegend?: boolean
  textAreaHeight?: number
  templateId?: string
  previewLocale?: string
  previewSubject?: string
  previewPdfWidth?: number
  previewPdfBaseHeight?: number
  previewPdfPerTicketHeight?: number
}

export const HtmlEditor: FC<Props> = ({
  label,
  name,
  variables,
  isError,
  control,
  value,
  onChange,
  checkboxLabel = 'Use Rich Text Editor (uncheck for plain text)',
  checkboxWrapperClassName,
  disabled = false,
  index,
  defaultCustomHTML = true,
  hideCheckbox = false,
  showPreview = false,
  showLegend = false,
  textAreaHeight = 9,
  templateId,
  previewLocale,
  previewSubject = '',
  previewPdfWidth,
  previewPdfBaseHeight,
  previewPdfPerTicketHeight,
}) => {
  const [showModal, setShowModal] = useState(false)
  const [editorInstance, setEditorInstance] = useState<CoreEditor | null>(null)
  const [isCustomHTML, setIsCustomHtml] = useState(defaultCustomHTML)
  const [Editor, setEditor] = useState<any>(null)
  const [editorError, setEditorError] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const editorWrapperRef = useRef<HTMLDivElement>(null)
  const savedTextareaSelection = useRef<{ start: number; end: number } | null>(
    null,
  )
  const [previewHtml, setPreviewHtml] = useState<string>(value)
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const id = useId()

  useEffect(() => {
    if (!showPreview) return
    if (!templateId) {
      setPreviewHtml(value)
      return
    }

    if (previewTimer.current) {
      clearTimeout(previewTimer.current)
    }

    previewTimer.current = setTimeout(async () => {
      try {
        const resp = await getEmailTemplateVelocityPreview(
          templateId,
          {
            template: value,
            customSubject: previewSubject,
            ...(previewPdfWidth !== undefined && {
              customPdfWidth: previewPdfWidth,
            }),
            ...(previewPdfBaseHeight !== undefined && {
              customPdfBaseHeight: previewPdfBaseHeight,
            }),
            ...(previewPdfPerTicketHeight !== undefined &&
              previewPdfPerTicketHeight !== 0 && {
                customPdfPerTicketHeight: previewPdfPerTicketHeight,
              }),
          },
          previewLocale,
        )
        if (resp.status === 200) {
          setPreviewHtml(resp.data)
        }
      } catch (e) {
        console.error(e)
      }
    }, 600)

    return () => {
      if (previewTimer.current) {
        clearTimeout(previewTimer.current)
      }
    }
  }, [
    value,
    showPreview,
    templateId,
    previewLocale,
    previewSubject,
    previewPdfWidth,
    previewPdfBaseHeight,
    previewPdfPerTicketHeight,
  ])

  useEffect(() => {
    // Skip loading if not using custom HTML
    if (!isCustomHTML) {
      setEditor(null)
      return
    }

    // Check if CKEditor is already loaded
    if ((window as any).ClassicEditor) {
      setEditor(() => (window as any).ClassicEditor)
      return
    }

    // Check if script is already in the DOM
    const existingScript = document.querySelector(
      'script[data-ckeditor-script="true"]',
    )
    if (existingScript) {
      // Wait for existing script to load
      const checkEditor = setInterval(() => {
        if ((window as any).ClassicEditor) {
          setEditor(() => (window as any).ClassicEditor)
          clearInterval(checkEditor)
        }
      }, 100)

      // Stop checking after 5 seconds
      setTimeout(() => clearInterval(checkEditor), 5000)
      return
    }

    // Load CKEditor dynamically via script tag
    const script = document.createElement('script')
    // Use absolute path from the public directory
    script.src = `${window.location.origin}/ckeditor/build/ckeditor.js`
    script.async = true
    script.setAttribute('data-ckeditor-script', 'true')

    script.onload = () => {
      if ((window as any).ClassicEditor) {
        setEditor(() => (window as any).ClassicEditor)
      } else {
        const error =
          'CKEditor script loaded but window.ClassicEditor is undefined'
        console.error(error)
        setEditorError(error)
      }
    }

    script.onerror = () => {
      const error = 'Failed to load CKEditor script'
      console.error(error)
      setEditorError(error)
    }

    document.head.appendChild(script)

    // Don't remove the script on cleanup to avoid re-loading
    return () => {
      // Reset editor state but keep the script loaded
      setEditor(null)
    }
  }, [isCustomHTML])

  return (
    <>
      <FormGroup
        label={label}
        labelWrapperClassName="justify-between"
        addon={
          <>
            {variables.length > 0 && (
              <Button
                onMouseDown={() => {
                  if (
                    !isCustomHTML &&
                    textareaRef.current &&
                    document.activeElement === textareaRef.current
                  ) {
                    savedTextareaSelection.current = {
                      start: textareaRef.current.selectionStart,
                      end: textareaRef.current.selectionEnd,
                    }
                  } else {
                    savedTextareaSelection.current = null
                  }
                }}
                onClick={() => {
                  setShowModal(true)
                }}
                type="button"
                variant="secondary"
                disabled={disabled}
              >
                Insert Variable
              </Button>
            )}
          </>
        }
        inputWrapperClassName="overflow-visible"
      >
        <div className={cn('w-full', showPreview && 'flex items-start gap-4')}>
          <div className={cn(showPreview && 'flex-1 min-w-0')}>
            {!isCustomHTML ? (
              <Textarea
                ref={textareaRef}
                value={value}
                onChange={(data) => {
                  onChange(data.target.value)
                }}
                rows={textAreaHeight}
                placeholder="Enter Description"
                className={cn(showPreview && 'min-h-[1032px]')}
              />
            ) : editorError ? (
              <div className="p-4 border border-destructive rounded bg-destructive/10 text-destructive">
                <p className="font-semibold">Failed to load editor</p>
                <p className="text-sm">{editorError}</p>
                <p className="text-sm mt-2">
                  Try unchecking &quot;Use Rich Text Editor&quot; to use plain
                  text mode.
                </p>
              </div>
            ) : Editor ? (
              <Controller
                name={name}
                control={control}
                defaultValue=""
                render={() => {
                  return (
                    <div
                      ref={editorWrapperRef}
                      className={cn(
                        'long-ck-editor ck-body-wrapper w-full',
                        isError && 'border-accent-3',
                        showPreview && 'long-ck-editor-preview',
                      )}
                    >
                      <CKEditor<CoreEditor>
                        editor={
                          Editor as unknown as {
                            create(...args: unknown[]): Promise<CoreEditor>
                          }
                        }
                        data={value}
                        onReady={(editor) => {
                          setEditorInstance(editor)
                          // CKEditor mounts every instance's popups into the first
                          // .ck-body-wrapper on the page, so later editors (in their own
                          // stacking contexts, e.g. blurred cards) cover them. Keep each
                          // editor's popups next to it.
                          const popups =
                            editor.ui.view.body.bodyCollectionContainer
                          if (popups)
                            editorWrapperRef.current?.appendChild(popups)
                        }}
                        onChange={(event, editor) => {
                          const data = editor.getData()
                          onChange(data)
                        }}
                        disabled={disabled}
                      />
                    </div>
                  )
                }}
              />
            ) : (
              <div className="p-4 border border-monochrome-3 rounded text-monochrome-2">
                Loading editor...
              </div>
            )}
          </div>
          {showPreview && (
            <div className="w-[40%] border border-monochrome-3-60 rounded-[30px] bg-gradient-4">
              <div className="p-4">
                <PageTitle
                  title={
                    <p className="text-subtitle-m">
                      Email <span className="text-accent-1">Preview</span>
                    </p>
                  }
                  icon="email-preview"
                />
              </div>
              <div className="p-4">
                <iframe
                  title={`html-editor-preview-${index}`}
                  srcDoc={`<style>
                    *{scrollbar-width:thin;scrollbar-color:hsl(136,100%,51%) transparent;}
                    *::-webkit-scrollbar{width:2px;height:2px;background-color:transparent;}
                    *::-webkit-scrollbar-track{background:hsla(0,0%,100%,0.2);border-radius:3px;}
                    *::-webkit-scrollbar-thumb{background-color:hsl(136,100%,51%);border-radius:3px;border:none;}
                    *::-webkit-scrollbar-thumb:hover{background-color:hsl(138,72%,35%);}
                  </style>${previewHtml}`}
                  className="w-full h-[860px]"
                />
              </div>
            </div>
          )}
        </div>
      </FormGroup>
      {!hideCheckbox && (
        <FormGroup
          direction="row"
          labelClassName="whitespace-nowrap"
          inputWrapperClassName="w-auto"
          wrapperClassName={cn(
            'w-auto flex-row-reverse justify-end mt-2',
            checkboxWrapperClassName,
          )}
          label={checkboxLabel}
          labelProps={{
            htmlFor: id,
          }}
        >
          <Checkbox
            id={id}
            className="border-monochrome-1"
            checked={isCustomHTML}
            onCheckedChange={(checked) => {
              setIsCustomHtml(!!checked)
            }}
          />
        </FormGroup>
      )}
      {showLegend && variables.length > 0 && (
        <div className="mt-4">
          <p className="text-body-medium text-monochrome-2 mb-2">Legend</p>
          <div className="flex flex-wrap gap-2">
            {variables.map((variable) => (
              <span
                key={variable.key}
                title={variable.description}
                className="px-[15px] py-[15px] rounded-2xl text-xs font-medium bg-monochrome-3-60 text-monochrome-1 border border-monochrome-120"
              >
                {variable.key}
              </span>
            ))}
          </div>
        </div>
      )}

      <InsertVariableModal
        showModal={showModal}
        setShowModal={setShowModal}
        variables={variables}
        setInsertVariable={(val) => {
          if (isCustomHTML) {
            if (editorInstance) {
              editorInstance.model.change((writer) => {
                editorInstance.model.insertContent(
                  writer.createText(val),
                  editorInstance.model.document.selection,
                )
              })
            }
          } else {
            const selection = savedTextareaSelection.current
            if (selection) {
              const newValue =
                value.slice(0, selection.start) +
                val +
                value.slice(selection.end)
              const cursorAfterInsert = selection.start + val.length
              onChange(newValue)
              requestAnimationFrame(() => {
                const el = textareaRef.current
                if (el) {
                  el.focus()
                  el.setSelectionRange(cursorAfterInsert, cursorAfterInsert)
                }
              })
              savedTextareaSelection.current = null
            } else {
              onChange(value + val)
            }
          }
          setShowModal(false)
        }}
      />
    </>
  )
}
